import { useMemo } from 'react'
import { useRouter } from 'next/router'
import { JackpotInfo, PrizesInfo, SimpleRound } from 'types/types'
import { GameStatusType } from 'api/constants'

export interface Game {
  id: number
  title: string
  status: GameStatusType
  prize: string
  description: string | null
  deadline: string
  is_in_progress: boolean
  is_user_joined?: boolean
  position?: number | null
  score?: number | null
  winner_name?: string | null
  winner_score?: number | null
  jackpot?: JackpotInfo | null
  prizes?: PrizesInfo | null
  sport_name?: string | null
  sport_image?: string | null
  has_multiple_rounds?: boolean
  next_round_start_time?: string | null
  next_round_name?: string | null
  next_round_is_user_joined?: boolean
  rounds_joined?: { round_id: number; is_user_joined: boolean }[]
  rounds?: SimpleRound[]
  tournament_image?: string | null
  tournament_name?: string | null
  is_battle_royale?: boolean
  participant_count?: number | null
}

export interface PartnerGamesResponse {
  partner_id: number
  games: Game[]
  total: number
  filters: {
    statuses: string[]
    sports: { name: string }[]
    tournaments: { name: string; image_url: string | null }[]
  }
}

// The initial (unfiltered) games list already contains every sport/tournament
// for the partner — sport and tournament filtering is done client-side over
// that same list instead of round-tripping to the server on every filter
// click, mirroring how status filtering already works in landing.tsx.
export const usePartnerGames = (partnerId: number, initialGame: Game[]) => {
  const router = useRouter()
  const sportName = router.query.sport as string | undefined
  const tournamentName = router.query.tournament as string | undefined

  const games = useMemo(() => {
    if (tournamentName) {
      return initialGame.filter((game) => game.tournament_name === tournamentName)
    }
    if (sportName) {
      return initialGame.filter(
        (game) => game.sport_name?.toLowerCase() === sportName.toLowerCase(),
      )
    }
    return initialGame
  }, [initialGame, sportName, tournamentName])

  return { games, loading: false, error: null }
}
